import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  BanknoteIcon,
  BellIcon,
  BookOpenTextIcon,
  BoxIcon,
  ChartNoAxesCombinedIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  HomeIcon,
  MailIcon,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  PlusCircleIcon,
  QrCodeIcon,
  TagIcon,
  TicketIcon,
  UsersIcon,
  ZapIcon,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { dashboardActions } from "../../../store/dashboard";
import { State } from "../../../store/store";
import { Logo } from "@/components/portal/Logo";
import { EventSelector } from "./EventSelector";
import { Link } from "react-router-dom";
import { AccountMenu } from "./account-menu/AccountMenu";
import { useDarkMode } from "@/hooks";
import { Organisation } from "@/models/Dashboard";
import { ORGANISATION_KEY } from "@/pages/protected-route";

export const Nav = () => {
  const dispatch = useDispatch();
  const navHidden = useSelector((state: State) => state.dashboard.navHidden);

  const [isHovered, setIsHovered] = useState(false);
  const hoverZoneRef = useRef(null);
  const hoverNavRef = useRef(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const setNavHidden = (hidden: boolean) => {
    dispatch(dashboardActions.SetNavHidden({ navHidden: hidden }));
  };

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Handle mouse enter and leave
  const handleMouseEnter = () => {
    if (navHidden) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = (e) => {
    if (hoverNavRef.current && !hoverNavRef.current.contains(e.relatedTarget)) {
      setIsHovered(false);
    }
  };

  // Ensure only one instance is ever mounted
  useEffect(() => {
    if (!navHidden) {
      setIsHovered(false);
    }
  }, [navHidden]);

  const sidebarOpen = useSelector(
    (state: State) => state.dashboard.sidebarOpen
  );
  const darkMode = useDarkMode();

  const setSidebarOpen = (sidebarOpen: boolean) => {
    dispatch(dashboardActions.SetSideBarOpen({ sidebarOpen }));
  };

  const organisation: Organisation = JSON.parse(
    localStorage.getItem(ORGANISATION_KEY) || "{}"
  );

  const featureFlags = {
    accreditationEmails: organisation.accreditationEmailsEnabled,
    accreditationGroups: organisation.accreditationGroupsEnabled,
    flows: organisation.flowsEnabled,
    forms: organisation.formsEnabled,
    ticketing: organisation.ticketingEnabled,
  };

  const NAVIGATION_GROUPS = [
    {
      name: "Ticketing",
      items: [
        {
          name: "Home",
          href: "/dashboard",
          icon: HomeIcon,
        },
        {
          name: "Tickets",
          href: "/dashboard/tickets",
          icon: TicketIcon,
        },
        {
          name: "Addons",
          href: "/dashboard/addons",
          icon: PlusCircleIcon,
        },
        {
          name: "Promo Codes",
          href: "/dashboard/promo-codes",
          icon: TagIcon,
        },
        {
          name: "Orders",
          href: "/dashboard/orders",
          icon: BanknoteIcon,
        },
        {
          name: "Attendees",
          href: "/dashboard/attendees",
          icon: QrCodeIcon,
        },
        {
          name: "Reporting",
          href: "/dashboard/reporting",
          icon: ChartNoAxesCombinedIcon,
        },
      ],
    },
    {
      name: "Accreditation",
      items: [
        {
          name: "Groups",
          href: "/dashboard/accreditation/groups",
          icon: BoxIcon,
        },
        {
          name: "Emails",
          href: "/dashboard/accreditation/emails",
          icon: MailIcon,
        },
        {
          name: "Forms",
          href: "/dashboard/forms",
          icon: BookOpenTextIcon,
        },
        {
          name: "Flows",
          href: "/dashboard/flows",
          icon: ZapIcon,
        },
      ],
    },
    {
      name: "Platform",
      items: [
        {
          name: "Users",
          href: "/dashboard/users",
          icon: UsersIcon,
          children: [
            {
              name: "Users",
              href: "/dashboard/users",
            },
            {
              name: "Groups",
              href: "/dashboard/groups",
            },
          ],
        },
      ],
    },
  ];

  const filteredNavigation = NAVIGATION_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (group.name === "Accreditation") {
        if (item.name === "Groups" && !featureFlags.accreditationGroups)
          return false;
        if (item.name === "Emails" && !featureFlags.accreditationEmails)
          return false;
        if (item.name === "Flows" && !featureFlags.flows) return false;
        if (item.name === "Forms" && !featureFlags.forms) return false;
      }
      if (group.name === "Ticketing") {
        // Always return tickets as we always need them
        if (item.name === "Tickets") return true;
        // if it's anything else, ticketing must be enabled
        if (featureFlags.ticketing) return true;
        return false;
      }
      return true;
    }),
  })).filter((group) => group.items.length > 0);

  const Item = ({ item }: any) => {
    const isCurrent =
      location.pathname === item.href ||
      (item.children &&
        item.children.some((child: any) => location.pathname === child.href));
    const [open, setOpen] = useState(isCurrent);

    const handleClick = () => {
      if (item.href) {
        setSidebarOpen(false); // Close the sidebar on item click
      }
    };

    return item.children ? (
      <div onClick={() => setOpen(!open)} className="">
        <div className="flex items-center text-sm group dark:hover:bg-white/10 hover:bg-gray-100 p-1.5 rounded-md cursor-pointer text-gray-500 dark:text-white/80">
          <div>
            <item.icon className="h-[1.1rem] h-[1.1rem] shrink-0 dark:text-white/80" />
          </div>
          <div className="ml-2">{item.name}</div>
          <div className="flex flex-1 justify-end text-gray-400">
            {open ? (
              <ChevronDownIcon className="h-5 w-5 shrink-0 dark:text-white" />
            ) : (
              <ChevronRightIcon className="h-5 w-5 shrink-0 dark:text-white" />
            )}
          </div>
        </div>
        {open && (
          <div className="border-l dark:border-white/15 border-gray-300 ml-3.5 mt-2">
            {item.children.map((child: any) => (
              <Link
                to={child.href}
                onClick={handleClick} // Close the sidebar when a child item is clicked
                className={`${
                  child.href == location.pathname ? "bg-white/10" : ""
                } ml-[0.6rem] flex items-center text-sm dark:hover:bg-white/10 hover:bg-gray-100  text-gray-500 dark:text-white/80 px-2.5 py-1 rounded-md mb-1`}
              >
                {child.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    ) : (
      <Link
        to={item.href}
        onClick={handleClick} // Close the sidebar when the item is clicked
        className={`${
          item.href === location.pathname ? "bg-white/10" : ""
        } flex items-center text-sm group dark:hover:bg-white/10 hover:bg-gray-100 rounded-md ${
          sidebarCollapsed ? "p-1.5 py-[0.5rem]" : "p-1.5"
        }`}
      >
        <div>
          <item.icon
            className={` ${
              sidebarCollapsed
                ? "h-[1.5rem] w-[1.5rem]"
                : "h-[1.1rem] h-[1.1rem]"
            } shrink-0 dark:text-white text-gray-500`}
          />
        </div>
        {!sidebarCollapsed && (
          <div className="ml-2 dark:text-white/90 text-gray-500">
            {item.name}
          </div>
        )}
      </Link>
    );
  };

  const DesktopNav = () => {
    return (
      <div className="text-white h-full flex flex-col pt-4 relative">
        <div className="-ml-1 mr-1">
          <EventSelector sidebarCollapsed={sidebarCollapsed} />
        </div>

        <div
          className={`${
            false ? "bg-white/10" : ""
          } flex items-center text-sm group dark:hover:bg-white/10 hover:bg-gray-100 rounded-md mt-3 -mb-1 cursor-pointer ${
            sidebarCollapsed ? "p-1.5 py-[0.5rem]" : "p-1.5"
          }`}
        >
          <div>
            <BellIcon
              className={` ${
                sidebarCollapsed
                  ? "h-[1.5rem] w-[1.5rem]"
                  : "h-[1.1rem] h-[1.1rem]"
              } shrink-0 dark:text-white text-gray-500`}
            />
          </div>
          {!sidebarCollapsed && (
            <div className="ml-2 dark:text-white/90 text-gray-500">
              Notifications
            </div>
          )}
          {/* <div className="flex-1 justify-end flex">
            <div className="bg-zinc-800 inline-flex px-1.5 rounded-md">1</div>
          </div> */}
        </div>

        {/* Navigation */}
        <nav className="mt-4 px-2 overflow-y-scroll">
          <div className="mb-32">
            {filteredNavigation.map((group) => (
              <div key={group.name} className="mb-5">
                <div
                  data-sidebar="group-label"
                  className="text-stone-400 flex h-8 shrink-0 items-center rounded-md text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&amp;>svg]:size-4 [&amp;>svg]:shrink-0 group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0"
                >
                  {group.name}
                </div>
                <ul
                  data-sidebar="menu"
                  className={`${
                    sidebarCollapsed ? "" : "w-full"
                  } flex min-w-0 flex-col gap-1 text-white -mx-[0.35rem]`}
                >
                  {group.items.map((item) => (
                    <Item item={item} />
                  ))}
                </ul>
              </div>
            ))}

            {/* <div className="mb-64">&nbsp;</div> */}
          </div>
        </nav>
        <div className="absolute bottom-0 left-0 right-0 mb-8">
          <AccountMenu />
        </div>
      </div>
    );
  };

  const [isMounted, setIsMounted] = useState(false);

  // Mounting delay to avoid flicker
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsMounted(true);
    }, 10); // Tiny delay to avoid initial flicker

    return () => clearTimeout(timeout);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <div className="relative h-screen h-full">
      {/* Main Sidebar */}
      <motion.div
        initial={{ x: navHidden ? "-100%" : 0 }}
        animate={{ x: navHidden ? "-100%" : 0 }}
        transition={{ duration: 0.3, type: "spring" }}
        className="absolute left-0 top-0 bottom-0 w-64 dark:bg-dark-primary pl-4 py-4 z-10"
      >
        <div className="flex items-center pt-1 pr-1">
          <Logo />
          <button
            type="button"
            onClick={() => setNavHidden(!navHidden)}
            className="border dark:border-white/10 rounded-lg p-1.5 ml-auto bg-zinc-100 text-zinc-500 dark:bg-zinc-800 cursor-pointer hover:bg-white/15 transition-colors duration-200"
          >
            <PanelLeftClose className="text-white size-4 dark:text-white/75 text-zinc-600" />
          </button>
        </div>

        <DesktopNav />
      </motion.div>

      {/* Hover Zone */}
      {navHidden && (
        <div
          ref={hoverZoneRef}
          onMouseEnter={handleMouseEnter}
          className="absolute left-0 top-0 bottom-0 w-6 bg-transparent z-50"
        />
      )}

      {/* Hover Sidebar */}
      <AnimatePresence>
        {isHovered && navHidden && (
          <motion.div
            ref={hoverNavRef}
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.2, type: "tween" }}
            onMouseLeave={handleMouseLeave}
            className="absolute left-0 top-0 bottom-0 z-50"
          >
            <div className="w-[20rem] h-full p-4">
              <div className="dark:bg-dark-primary bg-gray-50 border rounded-lg dark:border-white/10 h-full p-4 shadow-lg">
                <div className="flex items-center pt-1 pr-1">
                  <Logo />
                  <button
                    type="button"
                    onClick={() => setNavHidden(!navHidden)}
                    className="border dark:border-white/10 rounded-lg p-1.5 ml-auto bg-zinc-100 text-zinc-500 dark:bg-zinc-800 cursor-pointer hover:bg-white/15 transition-colors duration-200"
                  >
                    <PanelLeftOpen className="text-white size-4 dark:text-white/75 text-zinc-600" />
                  </button>
                </div>

                <DesktopNav />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
